import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import BlockIcon from '@mui/icons-material/Block';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Button } from '@/components/ui/Button';
import { useAppSelector } from '@/hooks/useAppSelector';
import { usePermissions } from '@/hooks/usePermissions';
import { resolveAccessibleLandingPath } from '@/utils/pageAccessEngine';
import { useActiveModule } from '@/hooks/useActiveModule';

type WorkspaceStatusPageProps = {
  variant: 'access-denied' | 'not-found' | 'error';
  title: string;
  message: ReactNode;
  showPrevious?: boolean;
  dashboardPath?: string;
};

export function WorkspaceStatusPage({
  variant,
  title,
  message,
  showPrevious = false,
  dashboardPath,
}: WorkspaceStatusPageProps) {
  const navigate = useNavigate();
  const activeModule = useActiveModule();
  const enabledModules = useAppSelector((s) => s.auth.enabledModules);
  const accessibleModules = useAppSelector((s) => s.auth.accessibleModules);
  const entryModule = useAppSelector((s) => s.auth.entryModule);
  const { has, permissions } = usePermissions();
  const roleCode = useAppSelector((s) => s.auth.user?.roleCode);

  const landingPath =
    dashboardPath ??
    resolveAccessibleLandingPath(
      {
        permissions,
        roleCode,
        enabledModules,
        accessibleModules,
        entryModule,
        hasPermission: has,
      },
      activeModule,
    );

  const Icon =
    variant === 'access-denied' ? BlockIcon : variant === 'not-found' ? SearchOffIcon : ErrorOutlineIcon;

  return (
    <div className="page-content flex min-h-[50vh] flex-col items-center justify-center px-6 py-16 text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800">
        <Icon className="text-slate-500 dark:text-slate-400" sx={{ fontSize: 32 }} />
      </div>
      <h1 className="mt-6 text-2xl font-semibold text-slate-900 dark:text-slate-100">{title}</h1>
      <p className="mt-3 max-w-md text-sm leading-relaxed text-slate-600 dark:text-slate-400">{message}</p>
      <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
        <Button type="button" onClick={() => navigate(landingPath, { replace: true })}>
          Go to Dashboard
        </Button>
        {showPrevious && (
          <Button type="button" variant="secondary" onClick={() => navigate(-1)}>
            Previous Page
          </Button>
        )}
      </div>
    </div>
  );
}

export function AccessDeniedPage({ message }: { message?: string }) {
  const { t } = useTranslation('common');
  return (
    <WorkspaceStatusPage
      variant="access-denied"
      title={t('pages.accessDenied')}
      message={
        message ??
        'You do not have permission to access this page. Please contact your administrator if access is required.'
      }
    />
  );
}

export function NotFoundPage() {
  const { t } = useTranslation('common');
  return (
    <WorkspaceStatusPage
      variant="not-found"
      title={t('pages.pageNotFound')}
      message="The requested page does not exist."
      showPrevious
    />
  );
}
